/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Link, Typography2, breakpoints } from '@coursera/cds-core';
import { AssignmentIcon } from '@coursera/cds-icons';

import { useIntl } from 'bundles/page/lib/useIntl';
import { getHeadingStyle } from 'bundles/unified-description-page-common/components/modules/product-overview/details/Details';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';

import _t from 'i18n!nls/xdp';

type Props = {
  totalQuizzes: number;
  totalAssignments: number;
  containsAiGrading?: boolean;
};

const styles = {
  itemTitle: css`
    padding: var(--cds-spacing-100) 0;
  `,
  icon: css`
    display: block;
  `,
  aiDisclaimer: css`
    display: box;
    width: 100%;

    ${breakpoints.down('sm')} {
      margin-left: calc((var(--cds-spacing-400) + var(--cds-spacing-50)));
    }
  `,
};

const SyllabusContentDetailItem = ({ totalAssignments, totalQuizzes, containsAiGrading }: Props) => {
  const intl = useIntl();
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;

  if (totalQuizzes === 0 && totalAssignments === 0) {
    return null;
  }

  const assignmentText = intl.formatMessage(
    containsAiGrading
      ? _t('{totalAssignments, number} {totalAssignments, plural, =1 { assignment} other { assignments}}¹')
      : _t('{totalAssignments, number} {totalAssignments, plural, =1 { assignment} other { assignments}}'),
    {
      totalAssignments,
    }
  );

  const quizText = intl.formatMessage(
    _t('{totalQuizzes, number} {totalQuizzes, plural, =1 { quiz} other { quizzes}}'),
    { totalQuizzes }
  );

  let assignmentAndQuizText;
  if (totalAssignments !== 0 && totalQuizzes !== 0) {
    assignmentAndQuizText = intl.formatMessage(_t('{quizText}, {assignmentText}'), {
      assignmentText,
      quizText,
    });
  } else if (totalAssignments !== 0) {
    assignmentAndQuizText = assignmentText;
  } else {
    assignmentAndQuizText = quizText;
  }

  return (
    <React.Fragment>
      <AssignmentIcon size="large" css={styles.icon} />
      <Typography2 variant={getHeadingStyle(enableXDPRedesign)} css={styles.itemTitle} component="div">
        {_t('Assessments')}
      </Typography2>
      <Typography2 component="p" variant="bodySecondary" color="supportText">
        {assignmentAndQuizText}
      </Typography2>
      {containsAiGrading && (
        <Link href="#ai-disclaimer" variant="quiet" typographyVariant="bodySecondary" css={styles.aiDisclaimer}>
          {_t('AI Graded see disclaimer')}
        </Link>
      )}
    </React.Fragment>
  );
};

export default SyllabusContentDetailItem;
